/**
 * Image Generation Service
 * Multi-provider: Gemini Imagen, DALL-E, Midjourney (via API)
 *
 * Quality tiers and pricing (December 2025):
 *
 * GPT-Image-1 (OpenAI):
 * - low: ~$0.01-0.02/image (drafts, previews)
 * - medium: ~$0.04-0.07/image (standard use)
 * - high: ~$0.17-0.19/image (premium, photorealistic)
 *
 * Gemini Imagen (Google):
 * - imagen-3.0-generate-002: ~$0.03/image (current standard)
 * - imagen-4.0-fast-generate-001: ~$0.02/image (fast, budget)
 * - imagen-4.0-generate-001: ~$0.03/image (balanced)
 * - imagen-4.0-ultra-generate-001: ~$0.05/image (premium)
 */

// Image quality tiers configuration
export const IMAGE_QUALITY_TIERS = {
  // OpenAI GPT-Image-1 quality parameter
  dalle: {
    budget: { quality: 'low', cost: 0.015, label: 'Économique' },
    balanced: { quality: 'medium', cost: 0.05, label: 'Équilibré' },
    quality: { quality: 'high', cost: 0.18, label: 'Qualité' },
    premium: { quality: 'high', cost: 0.18, label: 'Premium' },
  },
  // Gemini native image generation (works with AI Studio keys)
  // Note: Imagen 3/4 (:predict) only works with Vertex AI, not AI Studio
  gemini: {
    budget: { model: 'gemini-2.0-flash-exp', cost: 0.039, label: 'Flash 2.0 Exp' },
    balanced: { model: 'gemini-2.5-flash-image', cost: 0.039, label: 'Flash 2.5' },
    quality: { model: 'gemini-2.5-flash-image', cost: 0.039, label: 'Flash 2.5' },
    premium: { model: 'gemini-3-pro-image-preview', cost: 0.08, label: 'Pro 3 (4K)' },
  },
}

// Map LLM presets to image quality tiers
export const PRESET_IMAGE_QUALITY = {
  economique: 'budget',
  equilibre: 'balanced',
  qualite: 'quality',
  maximum: 'premium',
}

/**
 * Get image quality settings based on current preset or manual setting
 * @param {string} provider - 'dalle' or 'gemini'
 * @returns {Object} Quality config { quality?, model?, cost, label }
 */
export function getImageQualityConfig(provider) {
  const settings = JSON.parse(localStorage.getItem('emd_settings') || '{}')

  // Check for manual image quality override
  if (settings.imageQuality && settings.imageQuality !== 'auto') {
    return IMAGE_QUALITY_TIERS[provider]?.[settings.imageQuality] || IMAGE_QUALITY_TIERS[provider]?.balanced
  }

  // Otherwise, derive from LLM preset
  const preset = settings.selectedPreset || 'equilibre'
  const qualityTier = PRESET_IMAGE_QUALITY[preset] || 'balanced'

  return IMAGE_QUALITY_TIERS[provider]?.[qualityTier] || IMAGE_QUALITY_TIERS[provider]?.balanced
}

// Known error types for better UX
export const IMAGE_ERRORS = {
  OPENAI_ORG_VERIFICATION: {
    code: 'openai_org_verification',
    title: 'Vérification OpenAI requise',
    message: 'Votre organisation OpenAI doit être vérifiée pour utiliser GPT-Image-1.',
    action: 'Vérifier mon organisation',
    actionUrl: 'https://platform.openai.com/settings/organization/general',
    note: 'Après vérification, comptez ~15 minutes pour la propagation.',
    fallback: 'gemini',
  },
  OPENAI_QUOTA_EXCEEDED: {
    code: 'openai_quota_exceeded',
    title: 'Quota OpenAI dépassé',
    message: 'Votre quota d\'images OpenAI est épuisé pour ce mois.',
    action: 'Voir les limites',
    actionUrl: 'https://platform.openai.com/settings/organization/limits',
    fallback: 'gemini',
  },
  GEMINI_BILLING_REQUIRED: {
    code: 'gemini_billing_required',
    title: 'Facturation Google requise',
    message: 'Ce modèle nécessite un compte Google Cloud avec facturation activée.',
    action: 'Activer la facturation',
    actionUrl: 'https://aistudio.google.com/',
    note: 'Dans AI Studio : Settings → Plan information → Set up Billing',
    fallback: 'dalle',
  },
  GEMINI_REGION_BLOCKED: {
    code: 'gemini_region_blocked',
    title: 'Non disponible dans votre région',
    message: 'La génération d\'images Gemini n\'est pas disponible en France/Europe.',
    action: 'Utiliser GPT-Image-1',
    actionUrl: null,
    note: 'Solution : utilisez un VPN (serveur US) ou GPT-Image-1 (OpenAI) à la place.',
    fallback: 'dalle',
  },
  GEMINI_QUOTA_EXCEEDED: {
    code: 'gemini_quota_exceeded',
    title: 'Quota Gemini dépassé',
    message: 'Votre quota d\'images Gemini est épuisé.',
    fallback: 'dalle',
  },
}

/**
 * Parse and enhance error from API response
 */
function parseImageError(provider, errorMessage) {
  const msg = errorMessage?.toLowerCase() || ''

  // OpenAI organization verification required
  if (provider === 'dalle' && msg.includes('organization must be verified')) {
    return {
      ...IMAGE_ERRORS.OPENAI_ORG_VERIFICATION,
      originalMessage: errorMessage,
    }
  }

  // OpenAI quota exceeded
  if (provider === 'dalle' && (msg.includes('quota') || msg.includes('rate limit'))) {
    return {
      ...IMAGE_ERRORS.OPENAI_QUOTA_EXCEEDED,
      originalMessage: errorMessage,
    }
  }

  // Gemini geographic restriction (France/Europe blocked)
  if (provider === 'gemini' && (msg.includes('not available in your country') || msg.includes('not available in your region'))) {
    return {
      ...IMAGE_ERRORS.GEMINI_REGION_BLOCKED,
      originalMessage: errorMessage,
    }
  }

  // Gemini billing required (model not found = billing not enabled)
  if (provider === 'gemini' && (msg.includes('is not found') || msg.includes('not supported for predict') || msg.includes('billing'))) {
    return {
      ...IMAGE_ERRORS.GEMINI_BILLING_REQUIRED,
      originalMessage: errorMessage,
    }
  }

  // Gemini quota exceeded
  if (provider === 'gemini' && (msg.includes('quota') || msg.includes('resource exhausted'))) {
    return {
      ...IMAGE_ERRORS.GEMINI_QUOTA_EXCEEDED,
      originalMessage: errorMessage,
    }
  }

  // Generic error
  return {
    code: 'unknown',
    title: `Erreur ${provider === 'dalle' ? 'OpenAI' : 'Gemini'}`,
    message: errorMessage,
  }
}

const PROVIDERS = {
  gemini: {
    name: 'Gemini Image',
    // Gemini native image generation (works with AI Studio keys)
    // Available: gemini-2.0-flash-exp, gemini-2.5-flash-image, gemini-3-pro-image-preview
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/models',
    model: 'gemini-2.5-flash-image', // Default stable model
    strengths: ['Photoréalisme', 'Portraits', 'Produits'],
    bestFor: ['photo', 'portrait', 'équipe', 'témoignage', 'produit'],
  },
  dalle: {
    name: 'GPT Image 1',
    endpoint: 'https://api.openai.com/v1/images/generations',
    strengths: ['Précision instructions', 'Texte lisible', 'Haute résolution', 'Photoréalisme'],
    bestFor: ['icon', 'icône', 'logo', 'schéma', 'diagram', 'process', 'photo'],
  },
  midjourney: {
    name: 'Midjourney',
    endpoint: null, // Via Discord or third-party API
    strengths: ['Artistique', 'Ambiance', 'Illustrations', 'Premium'],
    bestFor: ['hero', 'illustration', 'ambiance', 'artistique'],
  },
}

/**
 * Get API key for image provider
 */
function getApiKey(provider) {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  if (provider === 'gemini') return keys.gemini
  if (provider === 'dalle') return keys.openai
  return keys[provider]
}

/**
 * Recommend best AI for image type
 * @param {string} imageType - Type of image (hero, icon, photo, etc.)
 * @param {string} context - Additional context from alt text or surrounding HTML
 * @returns {Object} { provider, reason }
 */
export function recommendProvider(imageType, context = '') {
  const ctx = (context || '').toLowerCase()
  const type = (imageType || '').toLowerCase()

  // Photo/portrait → Gemini (best photorealism)
  if (ctx.includes('photo') || ctx.includes('portrait') || ctx.includes('équipe') || ctx.includes('témoignage')) {
    return { provider: 'gemini', reason: 'Meilleur photoréalisme pour portraits et photos' }
  }

  // Icons/logos → DALL-E (precision)
  if (type === 'icon' || ctx.includes('icône') || ctx.includes('logo') || ctx.includes('svg')) {
    return { provider: 'dalle', reason: 'Plus précis pour icônes et éléments graphiques' }
  }

  // Hero/illustrations → Midjourney (artistic)
  if (ctx.includes('hero') || ctx.includes('illustration') || ctx.includes('ambiance')) {
    return { provider: 'midjourney', reason: 'Excellence artistique pour visuels d\'ambiance' }
  }

  // Diagrams/schemas → DALL-E
  if (ctx.includes('schéma') || ctx.includes('diagram') || ctx.includes('process')) {
    return { provider: 'dalle', reason: 'Meilleur pour schémas et concepts' }
  }

  // Default: Gemini (free and good quality)
  return { provider: 'gemini', reason: 'Bon équilibre qualité/coût' }
}

/**
 * Generate image with Gemini 2.0 Flash (native image generation via generateContent)
 * Docs: https://ai.google.dev/gemini-api/docs/image-generation
 * Note: Imagen 3 (:predict) only works with Vertex AI, not AI Studio keys
 */
async function generateWithGemini(prompt, options = {}) {
  const apiKey = getApiKey('gemini')
  if (!apiKey) throw new Error('Gemini API key not configured')

  // Get quality configuration
  const qualityConfig = getImageQualityConfig('gemini')
  const model = qualityConfig?.model || PROVIDERS.gemini.model

  console.log(`🖼️ [GEMINI] Using model: ${model} (${qualityConfig?.label || 'default'})`)

  // Build Gemini generateContent endpoint
  const endpoint = `${PROVIDERS.gemini.endpoint}/${model}:generateContent?key=${apiKey}`

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{
        parts: [{ text: `Generate a high-quality image: ${prompt}` }]
      }],
      generationConfig: {
        responseModalities: ['TEXT', 'IMAGE'],
      },
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    const errorMessage = error.error?.message || `Gemini error: ${response.status}`
    const parsedError = parseImageError('gemini', errorMessage)

    const enhancedError = new Error(parsedError.message)
    enhancedError.code = parsedError.code
    enhancedError.title = parsedError.title
    enhancedError.action = parsedError.action
    enhancedError.actionUrl = parsedError.actionUrl
    enhancedError.note = parsedError.note
    enhancedError.fallback = parsedError.fallback
    enhancedError.originalMessage = parsedError.originalMessage

    throw enhancedError
  }

  const data = await response.json()

  // Gemini generateContent returns candidates[].content.parts[]
  // Each part has either { text } or { inlineData: { mimeType, data } }
  const parts = data.candidates?.[0]?.content?.parts || []
  const imagePart = parts.find(p => p.inlineData?.data)

  if (!imagePart?.inlineData) {
    throw new Error('No image data in Gemini response')
  }

  const { mimeType, data: base64Data } = imagePart.inlineData
  const url = `data:${mimeType};base64,${base64Data}`

  return {
    url,
    provider: 'gemini',
    model,
    quality: qualityConfig?.label || 'standard',
    estimatedCost: qualityConfig?.cost || 0.039, // ~$0.039/image for Gemini Flash
  }
}

/**
 * Generate image with GPT-Image-1 (OpenAI)
 */
async function generateWithDalle(prompt, options = {}) {
  const apiKey = getApiKey('dalle')
  if (!apiKey) throw new Error('OpenAI API key not configured')

  const { width = 1024, height = 1024 } = options

  // Get quality configuration (low/medium/high based on preset/settings)
  const qualityConfig = getImageQualityConfig('dalle')
  const quality = options.quality || qualityConfig?.quality || 'medium'

  console.log(`🖼️ [DALLE] Using quality: ${quality} (${qualityConfig?.label || 'default'}, ~$${qualityConfig?.cost || '?'}/img)`)

  // gpt-image-1 supports: 1024x1024, 1536x1024 (landscape), 1024x1536 (portrait), auto
  // Higher res available: 2048x2048, etc. but more expensive
  let size = '1024x1024'
  if (width > height) size = '1536x1024' // Landscape
  else if (height > width) size = '1024x1536' // Portrait

  const response = await fetch(PROVIDERS.dalle.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: 'gpt-image-1',
      prompt,
      n: 1,
      size,
      quality, // 'low', 'medium', 'high'
      output_format: 'png', // gpt-image-1 uses output_format instead of response_format
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    const errorMessage = error.error?.message || `DALL-E error: ${response.status}`
    const parsedError = parseImageError('dalle', errorMessage)

    // Create enhanced error with metadata
    const enhancedError = new Error(parsedError.message)
    enhancedError.code = parsedError.code
    enhancedError.title = parsedError.title
    enhancedError.action = parsedError.action
    enhancedError.actionUrl = parsedError.actionUrl
    enhancedError.note = parsedError.note
    enhancedError.fallback = parsedError.fallback
    enhancedError.originalMessage = parsedError.originalMessage

    throw enhancedError
  }

  const data = await response.json()

  // gpt-image-1 returns URL directly, or b64_json if requested
  const imageResult = data.data[0]
  const url = imageResult.b64_json
    ? `data:image/png;base64,${imageResult.b64_json}`
    : imageResult.url

  return {
    url,
    revisedPrompt: imageResult.revised_prompt,
    provider: 'dalle',
    quality,
    estimatedCost: qualityConfig?.cost || 0.05,
  }
}

/**
 * Main image generation function
 * @param {string} prompt - Image generation prompt
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated image data
 */
export async function generateImage(prompt, options = {}) {
  const { provider = 'gemini' } = options

  switch (provider) {
    case 'gemini':
      return generateWithGemini(prompt, options)
    case 'dalle':
      return generateWithDalle(prompt, options)
    case 'midjourney':
      throw new Error('Midjourney requires manual generation via Discord')
    default:
      throw new Error(`Unknown provider: ${provider}`)
  }
}

/**
 * Generate prompt for image based on context
 * @param {Object} imageInfo - Image info from HTML extraction
 * @param {Object} siteContext - Site branding/style context
 * @returns {string} Optimized prompt
 */
export function generatePrompt(imageInfo, siteContext = {}) {
  const { filename, alt, context, type } = imageInfo
  const { industry, style, colors } = siteContext

  let prompt = alt || filename.replace(/[-_]/g, ' ').replace(/\.\w+$/, '')

  // Add context
  if (industry) prompt += `, ${industry} industry`
  if (style) prompt += `, ${style} style`
  if (colors) prompt += `, color palette: ${colors}`

  // Add type-specific modifiers
  switch (type) {
    case 'hero':
      prompt += ', wide shot, professional, high quality, modern'
      break
    case 'icon':
      prompt += ', simple, clean, minimal, vector style'
      break
    case 'photo':
      prompt += ', realistic photograph, natural lighting, candid'
      break
    case 'illustration':
      prompt += ', digital illustration, artistic, detailed'
      break
  }

  return prompt
}

/**
 * Extract images from generated HTML files
 * @param {Object} files - { filename: htmlContent }
 * @returns {Array} Array of image info objects
 */
export function extractImagesFromHTML(files) {
  const images = []
  const seen = new Set()

  for (const [filename, content] of Object.entries(files)) {
    // Match all <img> tags first
    const imgTagRegex = /<img[^>]+>/gi
    let tagMatch

    while ((tagMatch = imgTagRegex.exec(content)) !== null) {
      const imgTag = tagMatch[0]

      // Extract src and alt from the tag (order-independent)
      const srcMatch = imgTag.match(/src=["']([^"']+)["']/)
      const altMatch = imgTag.match(/alt=["']([^"']*)["']/)

      if (!srcMatch) continue

      const src = srcMatch[1]
      const alt = altMatch ? altMatch[1] : ''

      // Skip external URLs, data URLs, placeholders
      if (src.startsWith('data:') || src.startsWith('http') || src.includes('placeholder')) {
        continue
      }

      const imgFilename = src.split('/').pop()

      if (!seen.has(imgFilename)) {
        seen.add(imgFilename)

        // Detect image type from filename and context
        const type = detectImageType(imgFilename, alt)

        images.push({
          filename: imgFilename,
          src,
          alt,
          page: filename,
          type,
          prompt: alt || imgFilename.replace(/[-_]/g, ' ').replace(/\.\w+$/, ''),
        })
      }
    }
  }

  return images
}

/**
 * Detect image type from filename and context
 */
function detectImageType(filename, alt = '') {
  const name = (filename + ' ' + alt).toLowerCase()

  if (name.includes('hero') || name.includes('banner')) return 'hero'
  if (name.includes('icon') || name.includes('icone')) return 'icon'
  if (name.includes('photo') || name.includes('portrait')) return 'photo'
  if (name.includes('logo')) return 'icon'
  if (name.includes('illustration') || name.includes('illus')) return 'illustration'
  if (name.includes('bg') || name.includes('background')) return 'background'

  return 'photo' // Default
}

/**
 * Get a random quality tier for anti-fingerprint rotation
 * Varies image quality across a site for more natural appearance
 * @param {string} provider - 'dalle' or 'gemini'
 * @returns {Object} Random quality config
 */
export function getRandomImageQuality(provider) {
  const tiers = Object.keys(IMAGE_QUALITY_TIERS[provider] || {})
  if (tiers.length === 0) return null

  const randomTier = tiers[Math.floor(Math.random() * tiers.length)]
  return {
    tier: randomTier,
    ...IMAGE_QUALITY_TIERS[provider][randomTier],
  }
}

/**
 * Get anti-fingerprint image settings
 * Rotates provider and quality for each image to create variation
 * @param {number} imageIndex - Current image index in batch
 * @param {string[]} availableProviders - List of available providers
 * @returns {Object} { provider, qualityConfig }
 */
export function getAntiFingerPrintImageConfig(imageIndex, availableProviders) {
  if (!availableProviders || availableProviders.length === 0) {
    return null
  }

  // Rotate between providers
  const provider = availableProviders[imageIndex % availableProviders.length]

  // Also vary quality within the provider (weighted towards balanced)
  const qualityWeights = { budget: 0.2, balanced: 0.5, quality: 0.3 }
  const rand = Math.random()
  let cumulative = 0
  let selectedQuality = 'balanced'

  for (const [tier, weight] of Object.entries(qualityWeights)) {
    cumulative += weight
    if (rand <= cumulative) {
      selectedQuality = tier
      break
    }
  }

  const qualityConfig = IMAGE_QUALITY_TIERS[provider]?.[selectedQuality] || IMAGE_QUALITY_TIERS[provider]?.balanced

  console.log(`🖼️ [ANTI-FP] Image #${imageIndex}: ${provider} @ ${selectedQuality}`)

  return {
    provider,
    qualityTier: selectedQuality,
    qualityConfig,
  }
}

export default {
  generateImage,
  generatePrompt,
  extractImagesFromHTML,
  recommendProvider,
  getImageQualityConfig,
  getRandomImageQuality,
  getAntiFingerPrintImageConfig,
  PROVIDERS,
  IMAGE_QUALITY_TIERS,
  PRESET_IMAGE_QUALITY,
}
